<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useDisplay } from "vuetify";
import { useRoute } from "vue-router";
import { usePublicPractice } from "@/composables/models/practice/usePractice";
import { useSafeAreaInsets } from "@/composables/shared/useSafeAreaInsets";

const props = defineProps<{
  fullscreen?: boolean;
}>();

const { smAndDown, mdAndDown, mdAndUp } = useDisplay();
const safeAreaInsets = useSafeAreaInsets();

const route = useRoute();
const assignedPractice = computed(
  () => (route.query.practice as string) || (route.query.owner_practice as string) || ""
);

const { data: practice } = usePublicPractice({
  id: assignedPractice,
  enabled: computed(() => !!assignedPractice.value),
});

const effectivePicture = computed(
  () => practice?.value?.picture || practice?.value?.company?.picture || null
);
const effectiveWebsite = computed(
  () => practice?.value?.website || practice?.value?.company?.website || null
);

const isScrolled = ref(false);
const compacted = computed(() => isScrolled.value || (props.fullscreen && smAndDown.value));

const NAVBAR_HEIGHT_FULL_PX = 78;
const VUETIFY_DEFAULT_TOOLBAR_HEIGHT_PX = 64;

const appBarHeightProp = computed(() => {
  const baseHeight =
    !props.fullscreen && !compacted.value
      ? NAVBAR_HEIGHT_FULL_PX
      : VUETIFY_DEFAULT_TOOLBAR_HEIGHT_PX;
  return baseHeight + safeAreaInsets.top.value;
});

const onScroll = () => {
  isScrolled.value = window.scrollY > 0;
};

onMounted(() => {
  window.addEventListener("scroll", onScroll);
  onScroll();
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScroll);
});
</script>

<template>
  <v-app-bar
    data-testid="navbar-public"
    :elevation="0"
    :class="[
      'tw:!transition-all tw:duration-500 tw:ease-in-out',
      {
        'tw:!border-b tw:!border-background-secondary': isScrolled && mdAndDown,
      },
    ]"
    :color="mdAndDown && compacted ? 'background' : 'surface'"
    :density="compacted ? 'compact' : 'default'"
    :height="appBarHeightProp"
    :style="{ position: 'fixed' }"
  >
    <v-container
      :class="[
        'tw:!py-0 tw:h-full',
        {
          'tw:!max-w-[1366px]': !fullscreen,
          'tw:!max-w-full tw:md:!px-10': fullscreen,
        },
      ]"
    >
      <div class="tw:flex tw:items-center tw:justify-between tw:h-full">
        <div
          :class="[
            'tw:flex tw:items-center tw:h-full',
            {
              'tw:gap-4': mdAndUp,
              'tw:gap-2': smAndDown,
            },
          ]"
        >
          <v-hover>
            <template v-slot:default="{ isHovering, props }">
              <a
                href="https://www.cotreatme.com/"
                class="tw:flex tw:w-[150px] tw:max-w-full"
                v-bind="props"
                target="_blank"
              >
                <img
                  src="/logo_color.svg"
                  :class="[
                    'tw:w-full tw:max-w-[150px] tw:h-auto tw:object-contain tw:transition-transform tw:duration-250 tw:ease-in-out tw:will-change-transform',
                    {
                      'tw:scale-100': !isHovering,
                      'tw:scale-105': isHovering,
                    },
                  ]"
                />
              </a>
            </template>
          </v-hover>
          <v-divider
            v-if="effectivePicture || effectiveWebsite"
            vertical
            class="tw:!h-8 tw:!self-center tw:!mx-4"
            color="primary"
            thickness="1"
            opacity="0.25"
          />
          <template
            v-if="effectivePicture || effectiveWebsite"
            :key="practice?.id"
          >
            <v-hover>
              <template v-slot:default="{ isHovering, props: hoverProps }">
                <component
                  :is="effectiveWebsite ? 'a' : 'div'"
                  v-bind="hoverProps"
                  :href="effectiveWebsite ?? undefined"
                  :target="effectiveWebsite ? '_blank' : undefined"
                  :rel="effectiveWebsite ? 'noopener noreferrer' : undefined"
                  :class="[
                    'tw:flex tw:items-center tw:h-full',
                    { 'tw:cursor-pointer': effectiveWebsite },
                  ]"
                  :aria-label="effectiveWebsite ? `Visit ${practice?.name}` : practice?.name"
                >
                  <img
                    v-if="effectivePicture"
                    :src="effectivePicture.file"
                    :class="[
                      'tw:max-h-9 tw:transition-transform tw:duration-250 tw:ease-in-out tw:will-change-transform',
                      {
                        'tw:scale-100': !isHovering,
                        'tw:scale-110': isHovering && !!effectiveWebsite,
                      },
                    ]"
                  />
                  <v-icon
                    v-else-if="effectiveWebsite"
                    icon="mdi-web"
                    :class="[
                      'tw:transition-transform tw:duration-250 tw:ease-in-out tw:will-change-transform',
                      {
                        'tw:scale-100': !isHovering,
                        'tw:scale-110': isHovering,
                      },
                    ]"
                    color="primary"
                    size="32"
                  />
                </component>
              </template>
            </v-hover>
          </template>
        </div>
        <div
          v-if="mdAndUp"
          class="tw:flex tw:items-center tw:gap-2"
        >
          <v-btn
            variant="text"
            href="https://www.cotreatme.com/"
            target="_blank"
          >
            {{ $t("generic.navbar.findMoreAboutUs") }}
            <v-icon
              icon="mdi-open-in-new"
              color="secondary"
              class="tw:!ml-2 tw:!-mb-0.5"
            />
          </v-btn>
        </div>
      </div>
    </v-container>
  </v-app-bar>
</template>

<style scoped lang="scss">
[data-testid="navbar-public"] {
  :deep(.v-toolbar__content) {
    padding-top: env(safe-area-inset-top);
  }
}
</style>
